/**
 * CP-15.4 — Admin: Marketplaces, per-MP title label overrides, access policy,
 * and listing admin status.
 *
 * Four sub-panels stacked vertically inside one AdminConsole tab:
 *   1. Marketplace registry — edit display_name, nav_label, description,
 *      display_order, active. Code is immutable.
 *   2. Per-MP title label overrides — pick a marketplace, see every canonical
 *      title, set/clear override + surface_visible. Canonical title_code stays
 *      the contract; this surface is presentation-only.
 *   3. Access policy editor — JSON editor over admin.platform_config
 *      ["marketplace.access_policy"]; backend validates marketplace_code and
 *      role membership.
 *   4. Listing admin — list/filter all listings; admin can flip any status
 *      including the listee-locked suspended / banned. Listees cannot
 *      self-unsuspend (enforced by CP-15.2's PUT path).
 *
 * Operator rules honored:
 *   §1 No hard-coded terminology — every label here is editable.
 *   §2/3 Canonical marketplace_code and title_code stay immutable.
 *   §4 Label overrides are display-only.
 *   §9 Listees cannot unsuspend themselves — surfaced as a hint in the UI.
 *  §10 Super_admin is the only role that reaches this tab (route gate +
 *      backend require_roles).
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";

const STATUSES = ["active", "inactive", "hidden", "suspended", "banned"];

export function AdminMarketplacesTab() {
  return (
    <div data-testid={TID.adminMpTab} className="space-y-8">
      <p className="text-sm text-[var(--avcp-ink-soft)]">
        Manage the multi-marketplace overlay. Canonical marketplace and title
        codes are immutable here — only display labels, navigation labels,
        descriptions, ordering, the access-policy JSON, and listing admin
        status can be edited. None of these edits touch the underlying
        Phase-12 ranking or qualification engine.
      </p>
      <MarketplaceRegistry />
      <TitleLabelOverrides />
      <AccessPolicyEditor />
      <ListingAdmin />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Marketplace registry
// ---------------------------------------------------------------------------

function MarketplaceRegistry() {
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState({});
  const [busy, setBusy] = useState(null);

  const reload = async () => {
    try {
      const r = await api("/admin/marketplaces");
      setRows(r.items || []);
      setDirty({});
    } catch (e) {
      toast.error(e.message);
    }
  };
  useEffect(() => { reload(); }, []);

  const patch = (code, k, v) => {
    setRows((p) => p.map((r) => (r.code === code ? { ...r, [k]: v } : r)));
    setDirty((d) => ({ ...d, [code]: { ...(d[code] || {}), [k]: v } }));
  };

  const save = async (code) => {
    const body = dirty[code];
    if (!body) return;
    setBusy(code);
    try {
      await api(`/admin/marketplaces/${code}`, { method: "PATCH", body });
      toast.success(`Saved ${code}`);
      await reload();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="avcp-card p-5" data-testid={TID.adminMpRegistry}>
      <h2 className="text-lg font-semibold mb-3">Marketplaces</h2>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[var(--avcp-ink-soft)] border-b border-[var(--avcp-line)]">
              <th className="py-2 pr-3">Code</th>
              <th className="py-2 pr-3">Display name</th>
              <th className="py-2 pr-3">Nav label (override)</th>
              <th className="py-2 pr-3">Description</th>
              <th className="py-2 pr-3 w-16">Order</th>
              <th className="py-2 pr-3 w-16">Active</th>
              <th className="py-2 pr-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.code} data-testid={`${TID.adminMpRow}-${r.code}`} className="border-b border-[var(--avcp-line)]">
                <td className="py-2 pr-3 font-mono text-xs">{r.code}</td>
                <td className="py-2 pr-3">
                  <input className="avcp-input" data-testid={`${TID.adminMpDisplayName}-${r.code}`}
                         value={r.display_name || ""}
                         onChange={(e) => patch(r.code, "display_name", e.target.value)} />
                </td>
                <td className="py-2 pr-3">
                  <input className="avcp-input" placeholder="(falls back to display name)" data-testid={`${TID.adminMpNavLabel}-${r.code}`}
                         value={r.nav_label || ""}
                         onChange={(e) => patch(r.code, "nav_label", e.target.value)} />
                </td>
                <td className="py-2 pr-3">
                  <input className="avcp-input" data-testid={`${TID.adminMpDescription}-${r.code}`}
                         value={r.description || ""}
                         onChange={(e) => patch(r.code, "description", e.target.value)} />
                </td>
                <td className="py-2 pr-3">
                  <input type="number" className="avcp-input" data-testid={`${TID.adminMpOrder}-${r.code}`}
                         value={r.display_order} onChange={(e) => patch(r.code, "display_order", Number(e.target.value) || 0)} />
                </td>
                <td className="py-2 pr-3">
                  <input type="checkbox" data-testid={`${TID.adminMpActive}-${r.code}`}
                         checked={!!r.active} onChange={(e) => patch(r.code, "active", e.target.checked)} />
                </td>
                <td className="py-2 pr-3">
                  <button className="avcp-btn ghost" data-testid={`${TID.adminMpSave}-${r.code}`}
                          disabled={!dirty[r.code] || busy === r.code} onClick={() => save(r.code)}>
                    {busy === r.code ? "Saving…" : "Save"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 2. Title label overrides
// ---------------------------------------------------------------------------

function TitleLabelOverrides() {
  const [mps, setMps] = useState([]);
  const [mp, setMp] = useState("integrator");
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api("/admin/marketplaces");
        setMps(r.items || []);
      } catch (e) { toast.error(e.message); }
    })();
  }, []);

  const reload = async (code) => {
    try {
      const r = await api(`/admin/marketplaces/${code}/title-labels`);
      setRows(r.items || []);
      setDraft({});
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { reload(mp); }, [mp]);

  const upsert = async (titleCode) => {
    const d = draft[titleCode];
    const row = rows.find((r) => r.title_code === titleCode);
    const label = (d?.display_label ?? row?.override_label ?? "").toString().trim();
    const visible = d?.surface_visible ?? (row?.override_visible !== false);
    if (!label) {
      toast.error("Display label cannot be empty. Use Delete to clear an override.");
      return;
    }
    setBusy(titleCode);
    try {
      await api(`/admin/marketplaces/${mp}/title-labels/${titleCode}`, {
        method: "PUT",
        body: { display_label: label, surface_visible: !!visible },
      });
      toast.success(`Saved override for ${titleCode}`);
      await reload(mp);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const remove = async (titleCode) => {
    setBusy(titleCode);
    try {
      await api(`/admin/marketplaces/${mp}/title-labels/${titleCode}`, { method: "DELETE" });
      toast.success(`Removed override for ${titleCode}`);
      await reload(mp);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  return (
    <section className="avcp-card p-5" data-testid={TID.adminMpLabelsSection}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold">Per-marketplace title labels</h2>
        <label className="text-sm">
          <span className="mr-2 text-[var(--avcp-ink-soft)]">Marketplace:</span>
          <select className="avcp-input inline-block w-auto" data-testid={TID.adminMpLabelMpSelect}
                  value={mp} onChange={(e) => setMp(e.target.value)}>
            {mps.map((m) => <option key={m.code} value={m.code}>{m.display_name}</option>)}
          </select>
        </label>
      </div>
      <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
        Override is display-only. The canonical title code stays the contract for
        matching, ranking, and workforce-offering rows.
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[var(--avcp-ink-soft)] border-b border-[var(--avcp-line)]">
              <th className="py-2 pr-3">Title code</th>
              <th className="py-2 pr-3">Canonical label</th>
              <th className="py-2 pr-3">Override label</th>
              <th className="py-2 pr-3 w-20">Visible</th>
              <th className="py-2 pr-3 w-40"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.title_code} data-testid={`${TID.adminMpLabelRow}-${r.title_code}`}
                  className="border-b border-[var(--avcp-line)]">
                <td className="py-2 pr-3 font-mono text-xs">{r.title_code}</td>
                <td className="py-2 pr-3 text-[var(--avcp-ink-soft)]">{r.canonical_label}</td>
                <td className="py-2 pr-3">
                  <input className="avcp-input" placeholder="(canonical)"
                         data-testid={`${TID.adminMpLabelInput}-${r.title_code}`}
                         value={draft[r.title_code]?.display_label ?? r.override_label ?? ""}
                         onChange={(e) => setDraft((d) => ({ ...d, [r.title_code]: { ...(d[r.title_code] || {}), display_label: e.target.value } }))} />
                </td>
                <td className="py-2 pr-3">
                  <input type="checkbox" data-testid={`${TID.adminMpLabelVisible}-${r.title_code}`}
                         checked={draft[r.title_code]?.surface_visible ?? (r.override_visible !== false)}
                         onChange={(e) => setDraft((d) => ({ ...d, [r.title_code]: { ...(d[r.title_code] || {}), surface_visible: e.target.checked } }))} />
                </td>
                <td className="py-2 pr-3 space-x-2">
                  <button className="avcp-btn ghost" data-testid={`${TID.adminMpLabelSave}-${r.title_code}`}
                          disabled={busy === r.title_code} onClick={() => upsert(r.title_code)}>
                    {busy === r.title_code ? "Saving…" : "Save"}
                  </button>
                  {r.has_override && (
                    <button className="avcp-btn ghost" data-testid={`${TID.adminMpLabelDelete}-${r.title_code}`}
                            disabled={busy === r.title_code} onClick={() => remove(r.title_code)}>
                      Clear
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 3. Access policy editor
// ---------------------------------------------------------------------------

function AccessPolicyEditor() {
  const [policy, setPolicy] = useState("");
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const reload = async () => {
    try {
      const r = await api("/admin/access-policy");
      setPolicy(JSON.stringify(r.policy || {}, null, 2));
      setVersion(r.version || 0);
      setError(null);
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { reload(); }, []);

  const save = async () => {
    let parsed;
    try { parsed = JSON.parse(policy); }
    catch (e) { setError(`Invalid JSON: ${e.message}`); return; }
    setError(null);
    setBusy(true);
    try {
      await api("/admin/access-policy", { method: "PUT", body: { policy: parsed } });
      toast.success("Access policy updated");
      await reload();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <section className="avcp-card p-5" data-testid={TID.adminMpPolicy}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold">Access policy</h2>
        <span className="text-xs text-[var(--avcp-ink-soft)]">version {version}</span>
      </div>
      <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
        Role-driven, configurable. Each marketplace_code maps to{" "}
        <code>{`{ "view_roles": [...] }`}</code>. Backend validates marketplace codes and role names.
      </p>
      <textarea className="avcp-input font-mono text-xs" rows={12}
                data-testid={TID.adminMpPolicyEditor}
                value={policy}
                onChange={(e) => setPolicy(e.target.value)} />
      {error && <div className="text-rose-600 text-xs mt-1" data-testid={TID.adminMpPolicyError}>{error}</div>}
      <button className="avcp-btn primary mt-3" data-testid={TID.adminMpPolicySave}
              disabled={busy} onClick={save}>
        {busy ? "Saving…" : "Save access policy"}
      </button>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 4. Listing admin
// ---------------------------------------------------------------------------

function ListingAdmin() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ marketplace: "", status_filter: "", owner_kind: "" });
  const [busy, setBusy] = useState(null);
  const [drafts, setDrafts] = useState({});

  const reload = async () => {
    const qs = Object.entries(filter)
      .filter(([_, v]) => v)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join("&");
    try {
      const r = await api(`/admin/marketplace-listings${qs ? `?${qs}` : ""}`);
      setRows(r.items || []);
      setDrafts({});
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter]);

  const save = async (id) => {
    const status = drafts[id];
    if (!status) return;
    setBusy(id);
    try {
      await api(`/admin/marketplace-listings/${id}`, { method: "PATCH", body: { status } });
      toast.success(`Listing ${id} → ${status}`);
      await reload();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const ownerLabel = (r) =>
    r.contractor_user_id ? `user · ${r.user_email || r.contractor_user_id}` :
    r.org_id ? `org · ${r.org_name || r.org_id}` : "—";

  return (
    <section className="avcp-card p-5" data-testid={TID.adminMpListings}>
      <h2 className="text-lg font-semibold mb-3">Listing admin</h2>
      <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
        Operator rule §9: listees cannot self-unsuspend / unban. Use this panel
        to lift a suspension or ban.
      </p>
      <div className="flex flex-wrap gap-3 mb-3 text-sm">
        <select className="avcp-input w-auto" value={filter.marketplace}
                onChange={(e) => setFilter((f) => ({ ...f, marketplace: e.target.value }))}>
          <option value="">Any marketplace</option>
          <option value="integrator">Integrator</option>
          <option value="workforce">Workforce</option>
          <option value="event_tech">Event Tech</option>
        </select>
        <select className="avcp-input w-auto" value={filter.status_filter}
                onChange={(e) => setFilter((f) => ({ ...f, status_filter: e.target.value }))}>
          <option value="">Any status</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="avcp-input w-auto" value={filter.owner_kind}
                onChange={(e) => setFilter((f) => ({ ...f, owner_kind: e.target.value }))}>
          <option value="">Any owner kind</option>
          <option value="user">User (solo)</option>
          <option value="org">Org</option>
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[var(--avcp-ink-soft)] border-b border-[var(--avcp-line)]">
              <th className="py-2 pr-3">ID</th>
              <th className="py-2 pr-3">Marketplace</th>
              <th className="py-2 pr-3">Owner</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3 w-44">Set status</th>
              <th className="py-2 pr-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`${TID.adminMpListingRow}-${r.id}`}
                  className="border-b border-[var(--avcp-line)]">
                <td className="py-2 pr-3 font-mono text-xs">{r.id}</td>
                <td className="py-2 pr-3">{r.marketplace_code}</td>
                <td className="py-2 pr-3">{ownerLabel(r)}</td>
                <td className="py-2 pr-3 font-medium">{r.status}</td>
                <td className="py-2 pr-3">
                  <select className="avcp-input" data-testid={`${TID.adminMpListingStatus}-${r.id}`}
                          value={drafts[r.id] || r.status}
                          onChange={(e) => setDrafts((d) => ({ ...d, [r.id]: e.target.value }))}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="py-2 pr-3">
                  <button className="avcp-btn ghost" data-testid={`${TID.adminMpListingSave}-${r.id}`}
                          disabled={busy === r.id || !drafts[r.id] || drafts[r.id] === r.status}
                          onClick={() => save(r.id)}>
                    {busy === r.id ? "Saving…" : "Save"}
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="py-6 px-4 text-center text-[var(--avcp-ink-soft)]">
                  No listings match the current filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default AdminMarketplacesTab;
